import gsap from "gsap";
import { ReelsScene } from "onearm";

/**
 * Reels scene with tighter transition timings for Anubis.
 *
 * @extends ReelsScene
 */
export class AnubisReelsScene extends ReelsScene {
    /**
     * @param {Object} rest - Scene constructor options.
     */
    constructor(rest) {
        super(rest);

        const reelsConfig = this.services.get("gameConfig")?.reelsConfig ?? {};
        const sceneTimings = reelsConfig.sceneTimings ?? {};

        this.spinReelDelay = sceneTimings.spinReelDelay ?? 0.04;
        this.stopReelDelay = sceneTimings.stopReelDelay ?? 0.04;
        this.quickReelDelay = sceneTimings.quickReelDelay ?? 0.01;
    }

    /**
     * Starts symbol cascade with reduced column stagger.
     *
     * @param {string} [type="normal"] - Spin type.
     * @returns {gsap.core.Timeline}
     */
    spin(type = "normal") {
        const timeline = gsap.timeline();

        this.reels.goToIdle();
        timeline.playSfx("reel_spin");

        const instant = type === "turbo";
        const spinTimeline = gsap.timeline();

        for (let i = 0; i < this.reels.reels.length; i++) {
            const delay = instant ? 0 : this.spinReelDelay * i;
            spinTimeline.add(this.reels.reels[i].startSpin(type), delay);
        }

        timeline.add(spinTimeline, 0);

        return timeline;
    }

    /**
     * Drops final symbols with reduced startup and column delays.
     *
     * @param {Object} result - Spin result.
     * @param {boolean} [force=false] - Force final symbols without animation.
     * @param {string} [spinType="normal"] - Spin type.
     * @returns {gsap.core.Timeline|void}
     */
    stop(result, force = false, spinType = "normal") {
        if (force) {
            gsap.killTweensOf(this.reels);
            this.reels.reels.forEach(reel => reel.stop(result.matrix, force, spinType));
            return;
        }

        const timeline = gsap.timeline();
        const stopTimeline = gsap.timeline();

        timeline.stopSfx("reel_spin");

        this.reels.reels.forEach((reel, i) => {
            stopTimeline.add(reel.stop(result.matrix, false, spinType), this.stopReelDelay * i);
            stopTimeline.playSfx("reel_stop");
        });

        timeline.add(stopTimeline, 0);

        return timeline;
    }

    /**
     * Drops final symbols after user stop with reduced column delay.
     *
     * @param {Object} result - Spin result.
     * @param {string} [spinType="normal"] - Spin type.
     * @returns {gsap.core.Timeline}
     */
    quickStop(result, spinType = "normal") {
        gsap.killTweensOf(this.reels);

        const timeline = gsap.timeline();
        timeline.stopSfx("reel_spin");

        this.reels.reels.forEach((reel, i) => {
            gsap.killTweensOf(reel.children);
            timeline.add(reel.quickStop(result.matrix, i * this.quickReelDelay, spinType), 0);
        });

        timeline.playSfx("reel_stop");

        return timeline;
    }
}
